import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { MessageTemplate } from '../types/crm';
import { logger } from '../utils/logger';

// Helper function to create default templates manually if RPC doesn't exist
const createDefaultTemplatesManually = async (empresaId: string): Promise<void> => {
  const defaultTemplates = [
    {
      name: 'Saludo Inicial',
      icon: '👋',
      category: 'greeting',
      message: '¡Hola {nombre}! 👋\n\nSoy {usuario} de {empresa}.\n\n¿Cómo estás? Me gustaría conversar contigo sobre nuestros servicios.',
      empresa_id: empresaId,
      is_active: true,
      usage_count: 0,
    },
    {
      name: 'Seguimiento',
      icon: '🔄',
      category: 'followup',
      message: 'Hola {nombre},\n\nTe escribo para hacer seguimiento a nuestra última conversación.\n\n¿Tuviste oportunidad de revisar la información que te envié?',
      empresa_id: empresaId,
      is_active: true,
      usage_count: 0,
    },
    {
      name: 'Envío de Cotización',
      icon: '📄',
      category: 'quote',
      message: 'Hola {nombre}! \n\nTe adjunto la cotización que solicitaste. \n\nSi tienes alguna pregunta, con gusto te ayudo. ¿Cuándo podríamos conversar?',
      empresa_id: empresaId,
      is_active: true,
      usage_count: 0,
    },
    {
      name: 'Recordatorio de Pago',
      icon: '💰',
      category: 'payment',
      message: 'Hola {nombre},\n\nTe escribo para recordarte el pago pendiente.\n\n¿Necesitas ayuda con algo?',
      empresa_id: empresaId,
      is_active: true,
      usage_count: 0,
    },
    {
      name: 'Agradecimiento',
      icon: '🙏',
      category: 'closing',
      message: '¡Muchas gracias {nombre}! \n\nHa sido un placer trabajar contigo.\n\nCualquier cosa que necesites, aquí estamos. 😊',
      empresa_id: empresaId,
      is_active: true,
      usage_count: 0,
    },
    {
      name: 'Solicitar Reunión',
      icon: '📅',
      category: 'meeting',
      message: 'Hola {nombre},\n\nMe gustaría agendar una reunión contigo para conversar sobre nuestros servicios.\n\n¿Qué día y hora te viene mejor esta semana?',
      empresa_id: empresaId,
      is_active: true,
      usage_count: 0,
    },
    {
      name: 'Oferta Especial',
      icon: '🎁',
      category: 'promotion',
      message: '¡Hola {nombre}! 🎁\n\nTenemos una oferta especial solo para ti.\n\n¿Te interesa conocer más detalles?',
      empresa_id: empresaId,
      is_active: true,
      usage_count: 0,
    },
    {
      name: 'Respuesta Rápida',
      icon: '⚡',
      category: 'general',
      message: 'Hola {nombre},\n\nClaro, con gusto te ayudo.\n\n¿Qué necesitas?',
      empresa_id: empresaId,
      is_active: true,
      usage_count: 0,
    },
  ];

  // Insert all templates in a single batch
  const { error } = await supabase
    .from('message_templates')
    .insert(defaultTemplates);

  if (error) {
    logger.error('Error creating default templates manually:', error);
    throw error;
  }
};

interface MessageTemplatesState {
  templates: MessageTemplate[];
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchTemplates: (empresaId: string) => Promise<void>;
  createTemplate: (template: Omit<MessageTemplate, 'id' | 'created_at' | 'updated_at' | 'usage_count'>, empresaId: string) => Promise<MessageTemplate | null>;
  updateTemplate: (id: string, updates: Partial<MessageTemplate>, empresaId: string) => Promise<MessageTemplate | null>;
  deleteTemplate: (id: string, empresaId: string) => Promise<boolean>;
  incrementUsage: (id: string, empresaId: string) => Promise<void>;
  clearError: () => void;
}

export const useMessageTemplatesStore = create<MessageTemplatesState>((set, _get) => ({
  templates: [],
  isLoading: false,
  error: null,

  fetchTemplates: async (empresaId: string) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('message_templates')
        .select('*')
        .eq('empresa_id', empresaId)
        .eq('is_active', true)
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;

      // CRITICAL: If no templates found, auto-create default templates
      // This ensures EVERY empresa ALWAYS has templates, even if trigger failed
      if (!data || data.length === 0) {
        logger.warn('⚠️ No message templates found for empresa, auto-creating defaults...');
        try {
          // Call the database function to create default templates
          // Note: Function parameter name in PostgreSQL must match exactly
          const { error: createError } = await supabase.rpc('create_default_message_templates_for_empresa', {
            empresa_id_param: empresaId
          });

          if (createError) {
            logger.error('Error calling create_default_message_templates_for_empresa RPC:', createError);
            // Fallback: Create templates manually if RPC doesn't exist
            await createDefaultTemplatesManually(empresaId);
          } else {
            logger.info('✅ Default message templates created successfully');
            // Refetch templates after creation
            const { data: newData, error: refetchError } = await supabase
              .from('message_templates')
              .select('*')
              .eq('empresa_id', empresaId)
              .eq('is_active', true)
              .order('category', { ascending: true })
              .order('name', { ascending: true });

            if (!refetchError && newData) {
              set({ templates: newData, isLoading: false });
              return;
            }
          }
        } catch (createError: any) {
          logger.error('Error auto-creating templates:', createError);
          // Don't fail completely - just log the error
        }
      }

      set({ templates: data || [], isLoading: false });
    } catch (error: any) {
      logger.error('Error fetching message templates:', error);
      set({ error: error.message, isLoading: false });
    }
  },

  createTemplate: async (template, empresaId) => {
    try {
      const { data, error } = await supabase
        .from('message_templates')
        .insert({
          ...template,
          empresa_id: empresaId,
          usage_count: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        templates: [...state.templates, data],
      }));

      return data;
    } catch (error: any) {
      logger.error('Error creating message template:', error);
      set({ error: error.message });
      return null;
    }
  },

  updateTemplate: async (id, updates, empresaId) => {
    try {
      const { data, error } = await supabase
        .from('message_templates')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('empresa_id', empresaId)
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        templates: state.templates.map(t => t.id === id ? data : t),
      }));

      return data;
    } catch (error: any) {
      logger.error('Error updating message template:', error);
      set({ error: error.message });
      return null;
    }
  },

  deleteTemplate: async (id, empresaId) => {
    try {
      // Soft delete by setting is_active to false
      const { error } = await supabase
        .from('message_templates')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('empresa_id', empresaId);

      if (error) throw error;

      set(state => ({
        templates: state.templates.filter(t => t.id !== id),
      }));

      return true;
    } catch (error: any) {
      logger.error('Error deleting message template:', error);
      set({ error: error.message });
      return false;
    }
  },

  incrementUsage: async (id, empresaId) => {
    try {
      // Fetch current template to get current usage_count
      const { data: currentTemplate, error: fetchError } = await supabase
        .from('message_templates')
        .select('usage_count')
        .eq('id', id)
        .eq('empresa_id', empresaId)
        .single();

      if (fetchError) {
        logger.error('Error fetching template for usage increment:', fetchError);
        // Still update local state optimistically
        set(state => ({
          templates: state.templates.map(t =>
            t.id === id
              ? { ...t, usage_count: (t.usage_count || 0) + 1, last_used_at: new Date().toISOString() }
              : t
          ),
        }));
        return;
      }

      // Increment and update
      const newUsageCount = (currentTemplate?.usage_count || 0) + 1;
      const { error: updateError } = await supabase
        .from('message_templates')
        .update({
          usage_count: newUsageCount,
          last_used_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('empresa_id', empresaId);

      if (updateError) {
        logger.error('Error updating template usage:', updateError);
      }

      // Update local state
      set(state => ({
        templates: state.templates.map(t =>
          t.id === id
            ? { ...t, usage_count: newUsageCount, last_used_at: new Date().toISOString() }
            : t
        ),
      }));
    } catch (error: any) {
      logger.error('Error incrementing template usage:', error);
      // Don't throw - this is a non-critical operation
    }
  },

  clearError: () => set({ error: null }),
}));

